import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { UserResource } from "@exonn/js-sdk";
import { ModuleTabsStore } from "src/system/layout/ModuleTabsProvider.tsx";

import { createResourceStorageKeys } from "src/system/createResourceStorageKeys.ts";
import type { ModuleTabsPersistedState } from "src/user-data/types.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import type { Router } from "@remix-run/router";
import { sanitazeModuleLayoutTabs } from "src/user-data/sanitazors.ts";
import { getUserDataState } from "src/user-data/getUserDataState.ts";
import { ModuleLayoutTabsUserDataSchema } from "src/user-data/schemas.ts";
import { useUserDataApi } from "src/user-data/useUserDataApi.ts";
import { setUserDataCache } from "src/user-data/cache/setUserDataCache.ts";
import { deepEq, replacePathParams } from "@exonn/utils";
import { FormAction } from "src/components/Form/Form.types.ts";

import qs from "qs";
import { ResourceListSearchParams } from "src/system/useResourceList/useResourceList.types.ts";
import { convertGridFilterModelToUrlFilterModel } from "src/system/useResourceList/utils/convertGridFilterModelToUrlFilterModel.ts";

import { convertGridSortModelToUrlSortModel } from "src/system/useResourceList/utils/convertGridSortModelToUrlSortModel.ts";
import { getAllModulesIds } from "src/system/getAllModulesIds.ts";
import { GlobalModuleTabsStore } from "src/App/GlobalModuleTabsStore.ts";
import {
  type GlobalModuleTabsApi,
  GlobalModuleTabsContext,
  type OpenListParams,
  type OpenTabParams,
} from "./GlobalModuleTabsContext";

export const MockGlobalModuleTabsProvider = ({
  children,
  router,
}: PropsWithChildren<{
  router: Router;
}>) => {
  const [store] = useState<GlobalModuleTabsStore>(() => {
    return new GlobalModuleTabsStore({
      state: getAllModulesIds().reduce((acc, resource) => {
        return {
          ...acc,
          [resource]: new ModuleTabsStore({
            paths: [],
            router,
          }),
        };
      }, {}),
    });
  });

  const value = useMemo(
    () => ({
      store,
      api: {
        openTab: async () => {},
        openList: async () => {},
        getTabPath: () => "",
      },
    }),
    [store],
  );
  return (
    <GlobalModuleTabsContext.Provider value={value}>
      {children}
    </GlobalModuleTabsContext.Provider>
  );
};

export const GlobalModuleTabsProvider = ({
  children,
  user,
  router,
}: PropsWithChildren<{ user: UserResource; router: Router }>) => {
  const { setUserData } = useUserDataApi();
  const store = useMemo<GlobalModuleTabsStore>(() => {
    return new GlobalModuleTabsStore({
      state: getAllModulesIds().reduce((acc, resource) => {
        const storageKeys = createResourceStorageKeys();
        const modulePaths = createModuleRoutePaths({
          resource: resource,
        });

        const state = getUserDataState<ModuleTabsPersistedState>({
          user,
          key: storageKeys.tabs(resource),
          sanitaze: sanitazeModuleLayoutTabs(router),
          validationSchema: ModuleLayoutTabsUserDataSchema,
          defaultValue: {
            paths: [modulePaths.root],
          },
        });

        return {
          ...acc,
          [resource]: new ModuleTabsStore({
            paths: state.paths,
            editPath: modulePaths.edit.root,
            createPath: modulePaths.create.root,
            router,
          }),
        };
      }, {}),
    });
  }, [user, router]);

  useEffect(() => {
    const unsubscribers = Object.keys(store.state).map((resource) => {
      const moduleTabsStore = store.state[resource];
      const storageKeys = createResourceStorageKeys();
      return moduleTabsStore.emitter.on("pathsSet", () => {
        const modulePaths = createModuleRoutePaths({
          resource: resource,
        });
        const state = getUserDataState<ModuleTabsPersistedState>({
          user,
          key: storageKeys.tabs(resource),
          sanitaze: sanitazeModuleLayoutTabs(router),
          validationSchema: ModuleLayoutTabsUserDataSchema,
          defaultValue: {
            paths: [modulePaths.root],
          },
        });
        const value = {
          paths: moduleTabsStore.paths,
        };

        if (!deepEq(value, state)) {
          setUserDataCache<ModuleTabsPersistedState>({
            user,
            key: storageKeys.tabs(resource),
            value: value,
          });
          setUserData<ModuleTabsPersistedState>({
            key: storageKeys.tabs(resource),
            value: value,
          });
        }
      });
    });
    return () => {
      unsubscribers.forEach((unsubscriber) => {
        unsubscriber();
      });
    };
  }, [store, user, setUserData, router]);

  const getTabPath = useCallback(
    (params: OpenTabParams) => {
      const searchParams: Record<string, unknown> = {};
      if (params.type === "list") {
        const { filterModel, sortModel, page } = params;
        const moduleTabsStore = store.state[params.resource];
        let refresh = params.refresh;

        const existingPath = moduleTabsStore.paths[0];

        const modulePaths = createModuleRoutePaths({
          resource: params.resource,
        });

        if (filterModel) {
          searchParams[ResourceListSearchParams.FilterModel] =
            convertGridFilterModelToUrlFilterModel(filterModel);
        }

        if (sortModel) {
          searchParams[ResourceListSearchParams.SortModel] =
            convertGridSortModelToUrlSortModel(sortModel);
        }

        if (page != null) {
          searchParams[ResourceListSearchParams.Page] = page;
        }

        let path = modulePaths.root;

        if (Object.keys(searchParams).length) {
          refresh = true;
          path += `?${qs.stringify(searchParams, {
            encode: false,
          })}`;
        }

        if (!refresh && existingPath) {
          path = existingPath;
        }

        return path;
      }
      if (params.type === "form") {
        const modulePaths = createModuleRoutePaths({
          resource: params.resource,
        });
        const moduleTabsStore = store.state[params.resource];

        let path = modulePaths.create.root;

        let refresh = params.refresh;
        const searchParams = params.searchParams;
        if (searchParams) {
          refresh = true;
        }

        if (params.action === FormAction.Create) {
          const existingPath = moduleTabsStore.createPaths[0];

          if (!refresh && existingPath) {
            path = existingPath;
          }
        }

        if (params.action === FormAction.Edit) {
          // todo: check for existing path
          const tab = params.tab;
          const subTab = params.subTab;
          const subTabId = params.subTabId;

          if (tab) {
            if (subTab) {
              const isPathParam = subTab.startsWith(":");
              path = replacePathParams(
                modulePaths.edit.tab(tab).tab(subTab).root,
                {
                  id: params.id,
                  ...(isPathParam
                    ? {
                        [subTab.substring(1)]: subTabId,
                      }
                    : {}),
                },
              );
            } else {
              path = replacePathParams(modulePaths.edit.tab(tab).root, {
                id: params.id,
              });
            }
          } else {
            path = replacePathParams(modulePaths.edit.root, {
              id: params.id,
            });
          }
        }

        if (searchParams) {
          path = path + "?" + searchParams.toString();
        }
        return path;
      }
      throw new Error("undefined getTabPath type");
    },
    [store.state],
  );

  const openTab = useCallback(
    async (params: OpenTabParams) => {
      return router.navigate(getTabPath(params));
    },
    [router, getTabPath],
  );

  const openList = useCallback(
    async (params: OpenListParams) => {
      return openTab({
        ...params,
        type: "list",
      });
    },
    [openTab],
  );

  const api = useMemo<GlobalModuleTabsApi>(() => {
    return {
      openTab: openTab,
      openList: openList,
      getTabPath: getTabPath,
    };
  }, [openTab, openList, getTabPath]);

  const value = useMemo(
    () => ({
      store,
      api,
    }),
    [store, api],
  );
  return (
    <GlobalModuleTabsContext.Provider value={value}>
      {children}
    </GlobalModuleTabsContext.Provider>
  );
};
